/**
 * UNIVERSAL ARCHITECTURE - Main App
 * Pure Universal Architecture Application
 */

import { devWarn, devError } from './utils/devLog';
import React, { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { ProfilePage } from './pages/ProfilePage';
import { InvitesPage } from './pages/InvitesPage';
import { SharedChartPage } from './pages/SharedChartPage';
// Admin pages
import AdminLayout from './components/admin/AdminLayout';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { UsersPage as AdminUsersPage } from './pages/admin/UsersPage';
import { InvitesPage as AdminInvitesPage } from './pages/admin/InvitesPage';
import { StatsPage } from './pages/admin/StatsPage';
import { ActivityPage } from './pages/admin/ActivityPage';
import { SharedChartsPage } from './pages/admin/SharedChartsPage';
import { initializeStorage } from './services/StorageService';
import './App.css';

// Universal Architecture imports
import {
    initializeUniversalArchitecture,
    getUniversalArchitectureStatus,
    UNIVERSAL_DEV_UTILS
} from './components/common/AstrologyChart/universal_architecture';

// NEW ARCHITECTURE: Event-driven services
import { planetRenderingService } from './services/PlanetRenderingService';
import { muhurtaVisibilityService } from './services/MuhurtaVisibilityService';
import { initChakraEventSequenceManager } from './services/ChakraEventSequenceManager';
import { globalEventStream } from './components/common/AstrologyChart/universal_architecture';

/**
 * Universal Architecture App
 * Complete transition to Universal Architecture
 */
const UniversalApp: React.FC = () => {
    useEffect(() => {
        
        // Initialize Universal Architecture FIRST
        const initUniversalArchitecture = async () => {
            try {
                
                initializeUniversalArchitecture({
                    enableDebugMode: process.env.NODE_ENV === 'development',
                    enablePerformanceMonitoring: true,
                    enableLayerSystem: true,
                    customDataSchemas: {
                        'chart.configuration': {
                            type: 'object',
                            properties: {
                                divisionalType: { type: 'string' },
                                showLabels: { type: 'boolean' },
                                enableAnimations: { type: 'boolean' }
                            }
                        }
                    }
                });

                // Initialize ChakraEventSequenceManager (moved from EventStream to avoid circular dependency)
                initChakraEventSequenceManager(globalEventStream);

                // const status = getUniversalArchitectureStatus();

                // NEW ARCHITECTURE: Initialize event-driven services
                // Debug: Initializing event-driven services (production: logs disabled)

                // Services are automatically initialized when imported (singletons)
                // Just confirm they are ready

                if (process.env.NODE_ENV === 'development') {
                    // Attach global debugging utilities
                    window.UniversalApp = {
                        getStatus: getUniversalArchitectureStatus,
                        runDiagnostics: UNIVERSAL_DEV_UTILS.runDiagnostics,
                        resetArchitecture: UNIVERSAL_DEV_UTILS.resetArchitecture,
                        logInfo: UNIVERSAL_DEV_UTILS.logArchitectureInfo,
                        // NEW ARCHITECTURE: Add service debugging
                        services: {
                            planet: planetRenderingService,
                            muhurta: muhurtaVisibilityService
                        }
                    };

                }

            } catch (error) {
                devError('❌ [Universal App] Universal Architecture initialization failed:', error);
            }
        };

        // Initialize storage service
        const initStorage = async () => {
            try {
                const result = await initializeStorage();
                if (result.success) {
                } else {
                }
            } catch (error) {
                devError('❌ [Universal App] Storage service error:', error);
            }
        };

        // Initialize global user settings from localStorage
        const initGlobalSettings = () => {
            try {
                // Load showLogo setting
                const savedShowLogo = localStorage.getItem('user-settings-showLogo');
                const isGuestMode = window.sharedViewMode === 'guest';

                if (savedShowLogo !== null) {
                    window.globalShowLogo = savedShowLogo === 'true';
                } else {
                    // Default: true for guest mode, false for regular users
                    window.globalShowLogo = isGuestMode;
                }

                // Load centerImage setting (default: 'bhrigu')
                const savedCenterImage = localStorage.getItem('user-settings-centerImage');
                window.globalCenterImage = savedCenterImage || 'bhrigu';

                // Load rashiDisplayMode setting (default: 'symbols')
                const savedRashiMode = localStorage.getItem('user-settings-rashiDisplayMode');
                (window as any).globalRashiDisplayMode = savedRashiMode || 'symbols';
            } catch (error) {
                devWarn('⚠️ [Universal App] Failed to initialize global settings:', error);
                // Fallback to default
                window.globalShowLogo = false;
                window.globalCenterImage = 'bhrigu';
                (window as any).globalRashiDisplayMode = 'symbols';
            }
        };

        // Initialize both systems
        const initializeApp = async () => {
            // Initialize global settings FIRST (synchronous, must be ready before charts render)
            initGlobalSettings();

            await initUniversalArchitecture();
            await initStorage();

            // Initialize console API for chart sharing - DISABLED
            // initConsoleApi();

        };

        initializeApp();
    }, []);

    return (
        <AuthProvider>
            <BrowserRouter>
                <Box className="UniversalApp">
                    <Routes>
                        {/* Main Hora app - protected */}
                        <Route path="/" element={
                            <ProtectedRoute>
                                <MainLayout />
                            </ProtectedRoute>
                        } />

                        {/* Profile and settings routes - protected */}
                        <Route path="/profile" element={
                            <ProtectedRoute>
                                <ProfilePage />
                            </ProtectedRoute>
                        } />
                        <Route path="/invites" element={
                            <ProtectedRoute>
                                <InvitesPage />
                            </ProtectedRoute>
                        } />

                        {/* Shared chart route - public (guest access) */}
                        <Route path="/s/:code" element={<SharedChartPage />} />

                        {/* Admin Routes (Indra) - wrapped in AdminLayout */}
                        <Route path="/indra" element={
                            <AdminRoute>
                                <AdminLayout><AdminDashboardPage /></AdminLayout>
                            </AdminRoute>
                        } />
                        <Route path="/indra/users" element={
                            <AdminRoute>
                                <AdminLayout><AdminUsersPage /></AdminLayout>
                            </AdminRoute>
                        } />
                        <Route path="/indra/invites" element={
                            <AdminRoute>
                                <AdminLayout><AdminInvitesPage /></AdminLayout>
                            </AdminRoute>
                        } />
                        <Route path="/indra/stats" element={
                            <AdminRoute>
                                <AdminLayout><StatsPage /></AdminLayout>
                            </AdminRoute>
                        } />
                        <Route path="/indra/activity" element={
                            <AdminRoute>
                                <AdminLayout><ActivityPage /></AdminLayout>
                            </AdminRoute>
                        } />
                        <Route path="/indra/charts" element={
                            <AdminRoute>
                                <AdminLayout><SharedChartsPage /></AdminLayout>
                            </AdminRoute>
                        } />
                    </Routes>
                </Box>
            </BrowserRouter>
        </AuthProvider>
    );
};

export default UniversalApp;